import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import { ZuraChibi3DHost } from "./ZuraChibi3DHost";
import jawData from "./jaw_openings.json";

// Naskah subtitle sinkron per durasi detik
const SUBTITLE_CUES = [
  { start: 0, end: 4, text: "Kalo lo masih mikir A-I cuma bisa jawab chat doang..." },
  { start: 4, end: 7, text: "Liat layar ini. Di sini A-I gak cuma ngasih saran..." },
  { start: 7, end: 12, text: "Tapi udah bisa nge-remote HP fisik sendiri secara otonom!" },
  { start: 12, end: 17, text: "Mulai dari buka aplikasi Chrome di HP Samsung..." },
  { start: 17, end: 21, text: "Navigasi ke website, ngetik naskah, sampai klik tombol eksekusi..." },
  { start: 21, end: 26, text: "Tanpa ada satu pun jari manusia yang nyentuh layar HP!" },
  { start: 26, end: 30, text: "Semua digerakin murni lewat baris perintah di server." },
  { start: 30, end: 35, text: "Kebanyakan orang masih sibuk pamer prompt rahasia..." },
  { start: 35, end: 39.2, text: "Padahal masa depan udah pindah ke autonomous hardware agent!" },
];

export const ZuraClassaiPhoneDemo: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const timeSec = frame / fps;

  // Nilai pembukaan rahang riil dari gelombang audio
  const jawList = jawData.values as number[];
  const jawOpen = jawList[Math.min(frame, jawList.length - 1)] || 0.0;

  // Subtitle aktif
  const currentSub = SUBTITLE_CUES.find(
    (c) => timeSec >= c.start && timeSec < c.end
  );

  // Animasi floating & 3D tilt stage tengah
  const floatY = Math.sin(frame * 0.06) * 12;
  const tiltDeg = Math.sin(frame * 0.04) * 3;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#06070B",
        fontFamily: "'Inter', sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* 1. CYBER TECH GRID BACKGROUND */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(0, 240, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 240, 255, 0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          maskImage: "radial-gradient(ellipse at 50% 50%, black 40%, transparent 80%)",
        }}
      />

      {/* Glow Ambient Top & Center */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          left: "20%",
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(255, 107, 0, 0.12) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "35%",
          right: "-10%",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(0, 240, 255, 0.08) 0%, transparent 70%)",
          filter: "blur(70px)",
        }}
      />

      {/* 2. HEADER CLASSAI.ID STYLE */}
      <div
        style={{
          position: "absolute",
          top: 70,
          left: 60,
          right: 60,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              backgroundColor: "#FF6B00",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              fontSize: 20,
              color: "#000",
              boxShadow: "0 0 20px rgba(255,107,0,0.5)",
            }}
          >
            Z
          </div>
          <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>
            zura.ailab
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "rgba(255, 107, 0, 0.12)",
            border: "1px solid rgba(255, 107, 0, 0.4)",
            padding: "8px 18px",
            borderRadius: 30,
            fontSize: 18,
            fontWeight: 700,
            color: "#FF8C33",
            letterSpacing: 0.5,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#FF6B00",
              boxShadow: "0 0 10px #FF6B00",
            }}
          />
          JARANG YANG BAHAS
        </div>
      </div>

      {/* 3. HERO STAGE TENGAH (3D TILTED CARDS: TERMINAL SERVER + MOCKUP HP) */}
      <div
        style={{
          position: "absolute",
          top: 230,
          left: 60,
          right: 60,
          height: 980,
          perspective: 1200,
          zIndex: 20,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            transform: `translateY(${floatY}px) rotateY(${tiltDeg}deg) rotateX(4deg)`,
            transformStyle: "preserve-3d",
            position: "relative",
          }}
        >
          {/* Card 1: Terminal Otonom (Server Linux Control) */}
          <div
            style={{
              position: "absolute",
              top: 30,
              left: 20,
              width: 580,
              height: 520,
              backgroundColor: "rgba(13, 15, 22, 0.88)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: 20,
              boxShadow: "0 30px 60px rgba(0,0,0,0.7), 0 0 30px rgba(0,240,255,0.1)",
              backdropFilter: "blur(16px)",
              overflow: "hidden",
            }}
          >
            {/* Window Header */}
            <div
              style={{
                height: 48,
                backgroundColor: "rgba(24, 27, 38, 0.9)",
                display: "flex",
                alignItems: "center",
                padding: "0 18px",
                gap: 8,
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              <span
                style={{
                  marginLeft: 14,
                  fontSize: 16,
                  color: "#94A3B8",
                  fontFamily: "monospace",
                }}
              >
                zura-autonomous-node // bridge:8766
              </span>
            </div>

            {/* Code Body */}
            <div
              style={{
                padding: 22,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 16,
                lineHeight: 1.6,
                color: "#E2E8F0",
              }}
            >
              <div style={{ color: "#38BDF8" }}>
                &gt; POST /command HTTP/1.1
              </div>
              <div style={{ color: "#94A3B8" }}>
                Host: 127.0.0.1:8766 (Android Bridge)
              </div>
              <div style={{ marginTop: 12, color: "#F472B6" }}>
                {"{"}
              </div>
              <div style={{ paddingLeft: 20 }}>
                <span style={{ color: "#FDE047" }}>"action"</span>: <span style={{ color: "#4ADE80" }}>"launch"</span>,
              </div>
              <div style={{ paddingLeft: 20 }}>
                <span style={{ color: "#FDE047" }}>"package"</span>: <span style={{ color: "#4ADE80" }}>"com.android.chrome"</span>,
              </div>
              <div style={{ paddingLeft: 20 }}>
                <span style={{ color: "#FDE047" }}>"input_text"</span>: <span style={{ color: "#4ADE80" }}>"Autonomous Voice Script"</span>,
              </div>
              <div style={{ paddingLeft: 20 }}>
                <span style={{ color: "#FDE047" }}>"tap_coords"</span>: [540, 1065]
              </div>
              <div style={{ color: "#F472B6" }}>
                {"}"}
              </div>
              <div style={{ marginTop: 14, color: "#4ADE80" }}>
                ✔ WebSocket ACK: Samsung SM-A165F Executed!
              </div>
            </div>
          </div>

          {/* Card 2: Mockup Layar HP Samsung Beroperasi */}
          <div
            style={{
              position: "absolute",
              top: 140,
              right: 20,
              width: 360,
              height: 720,
              backgroundColor: "#0F121C",
              border: "3px solid #2A2F42",
              borderRadius: 36,
              boxShadow: "0 40px 80px rgba(0,0,0,0.85), 0 0 40px rgba(255,107,0,0.25)",
              overflow: "hidden",
            }}
          >
            {/* Phone Notch */}
            <div
              style={{
                height: 32,
                backgroundColor: "#171B26",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#06070B" }} />
            </div>

            {/* Screen Content */}
            <div style={{ padding: 20, textAlign: "center" }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: "#FF8C33",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  marginBottom: 16,
                }}
              >
                AUTONOMOUS PHONE AGENT
              </div>

              {/* Status Pill */}
              <div
                style={{
                  backgroundColor: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid #22C55E",
                  color: "#4ADE80",
                  padding: "6px 14px",
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 700,
                  display: "inline-block",
                  marginBottom: 24,
                }}
              >
                ● ACCESSIBILITY BRIDGE ONLINE
              </div>

              {/* Virtual App UI */}
              <div
                style={{
                  backgroundColor: "#1A1F2C",
                  borderRadius: 18,
                  padding: 16,
                  textAlign: "left",
                  fontSize: 13,
                  color: "#CBD5E1",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div style={{ fontWeight: 700, color: "#FFFFFF", marginBottom: 8 }}>
                  Target: Samsung SM-A165F
                </div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 12 }}>
                  Task: Auto Script Input & Synthesis
                </div>
                <div
                  style={{
                    backgroundColor: "#0A0D14",
                    padding: "10px 14px",
                    borderRadius: 10,
                    fontFamily: "monospace",
                    fontSize: 11,
                    color: "#38BDF8",
                    marginBottom: 14,
                  }}
                >
                  &gt; Tap: (540, 800)<br />
                  &gt; Type: "Mulai hari ini..."<br />
                  &gt; Tap: [BUAT SUARA]
                </div>

                <div
                  style={{
                    backgroundColor: "#FF6B00",
                    color: "#000",
                    fontWeight: 800,
                    textAlign: "center",
                    padding: "10px",
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  ⚡ EKSEKUSI OTONOM BERJALAN
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. SUBTITLE DINAMIS (KINETIC LOWER-THIRD KIRI BAWAH) */}
      <div
        style={{
          position: "absolute",
          bottom: 140,
          left: 60,
          width: 580,
          zIndex: 40,
        }}
      >
        {currentSub && (
          <div
            style={{
              backgroundColor: "rgba(10, 12, 18, 0.85)",
              borderLeft: "6px solid #FF6B00",
              padding: "20px 24px",
              borderRadius: "0 18px 18px 0",
              boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              style={{
                fontSize: 34,
                fontWeight: 900,
                lineHeight: 1.35,
                color: "#FFFFFF",
                letterSpacing: -0.5,
              }}
            >
              {currentSub.text}
            </div>
          </div>
        )}
      </div>

      {/* 5. AVATAR 3D ZURA (ANCHOR KANAN BAWAH BERBICARA LIP-SYNC REAL-TIME) */}
      <div
        style={{
          position: "absolute",
          bottom: -20,
          right: -40,
          width: 540,
          height: 680,
          zIndex: 35,
          pointerEvents: "none",
        }}
      >
        <ThreeCanvas
          width={540}
          height={680}
          camera={{ position: [0, 0, 2.6], fov: 38 }}
        >
          <ambientLight intensity={0.7} />
          <directionalLight position={[2, 3, 3]} intensity={1.5} />
          <pointLight position={[-2, 1, 2]} intensity={0.8} color="#00F0FF" />
          <pointLight position={[1, -1, 1]} intensity={0.6} color="#FF6B00" />
          
          <ZuraChibi3DHost frame={frame} jawOpen={jawOpen} />
        </ThreeCanvas>
      </div>

      {/* 6. AUDIO MASTER TRACK */}
      <Audio src={staticFile("vo_phone_agent_demo.mp3")} />
    </AbsoluteFill>
  );
};
